import { useEffect, useRef, useState, type JSX } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  COSMETIC_SLOTS,
  cosmeticById,
  cosmeticsBySlot,
  FURNITURE_CATALOG,
  FURNITURE_CATEGORIES,
  type Cosmetic,
  type CosmeticSlot,
  type EquippedCosmetics,
  type FurnitureCategory,
  type FurnitureItem,
} from '@duck/contracts';
import { api, ApiError } from '../../lib/api';
import { queryClient } from '../../lib/queryClient';
import { useAuthStore } from '../../state/auth.store';
import { CosmeticPreview } from './CosmeticPreview';

/**
 * M6 "Express Yourself" (emotion: Identity) — the Store + Wardrobe. A citizen spends EARNED coins to
 * make their duck their OWN, then everyone sees it. Cosmetic-only, never pay-to-win (Constitution
 * Art. 1 / docs/PHILOSOPHY.md): coins come from positive actions (server-attested) and buy nothing but
 * looks. The copy is kind and NON-PRESSURING — owning nothing is perfectly fine; this is play, not a
 * funnel.
 *
 * Flow: browse the catalog grouped by slot, each item showing a procedural PREVIEW (the same shapes
 * the duck wears, drawn as inline SVG so it's crisp + accessible), its price, and either a Buy button
 * (disabled with a gentle reason when already owned or short on coins) or — once owned — an
 * Equip/Unequip toggle (one per slot). Buying refetches the wallet; equipping persists to the gateway
 * AND re-broadcasts the new look over the realtime room via `onLookChange`.
 */

const SLOT_META: Record<CosmeticSlot, { icon: string; label: string }> = {
  hat: { icon: '🎩', label: 'Hats' },
  glasses: { icon: '🕶️', label: 'Glasses' },
  outfit: { icon: '🧣', label: 'Outfits' },
  pet: { icon: '🐤', label: 'Companions' },
};

/** Section headers for the Furniture tab, grouped by {@link FurnitureCategory} (matches the catalog). */
const FURNITURE_CATEGORY_META: Record<FurnitureCategory, { icon: string; label: string }> = {
  seating: { icon: '🛋️', label: 'Seating' },
  desks: { icon: '🖥️', label: 'Desks & work' },
  decor: { icon: '🗃️', label: 'Decor' },
  plants: { icon: '🪴', label: 'Plants' },
  wall: { icon: '🖼️', label: 'Wall art' },
  gaming: { icon: '🕹️', label: 'Gaming' },
  kitchen: { icon: '🍽️', label: 'Kitchen' },
  outdoor: { icon: '⛱️', label: 'Outdoor' },
};

function errMessage(e: unknown): string {
  return e instanceof ApiError ? e.message : 'Could not do that just now — try again in a moment.';
}

interface WardrobeProps {
  onClose: () => void;
  /** Broadcast the new equipped look over the realtime room so everyone sees it (M6). */
  onLookChange: (equipped: EquippedCosmetics) => void;
  /** Surface a small, friendly notice (reuses the HUD flashNotice). */
  onNotice: (message: string) => void;
  /**
   * Furniture Store (ТЗ): the player picked "Buy & Place" — hand the item up so the engine arms its
   * placement ghost (the buy is charged server-side only when the placement is confirmed). The parent
   * closes the Store so the player can see + click their room.
   */
  onArmFurniture: (item: FurnitureItem) => void;
}

/**
 * The Store + Wardrobe slide-over. Accessible name "Store & Wardrobe" (matches /shop|store|wardrobe|
 * cosmetics/i). Dual-named (button label + dialog aria-label), Esc-dismissable, focus managed — mirrors
 * the M5 guild `Panel`.
 */
export function Wardrobe({ onClose, onLookChange, onNotice, onArmFurniture }: WardrobeProps): JSX.Element {
  const reduceMotion = useReducedMotion();
  const sectionRef = useRef<HTMLElement>(null);
  // Two tabs: "Looks" (the existing cosmetics — skins/items) and "Furniture" (buy & place in the room).
  const [tab, setTab] = useState<'looks' | 'furniture'>('looks');

  // Focus management (WCAG 2.1.1 / 2.4.3): pull focus into the panel on open (so Esc works + a screen
  // reader announces the dialog), restore it to the opener on close. Not a full trap — a non-blocking
  // slide-over beside a still-interactive world (mirrors GuildPanel.Panel).
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const section = sectionRef.current;
    const target = section?.querySelector<HTMLElement>('[data-panel-close]') ?? section;
    target?.focus();
    return () => {
      if (opener && opener.isConnected && typeof opener.focus === 'function') opener.focus();
    };
  }, []);

  // Per-room wallet (ТЗ §7): key the inventory by the CURRENT room (workspaceId) so it never shows
  // another room's coins/owned items and an in-flight response from a prior room can't overwrite it.
  const roomKey = useAuthStore((s) => s.user?.workspaceId ?? null);
  const inventory = useQuery({
    queryKey: ['inventory', roomKey],
    queryFn: api.cosmeticsInventory,
  });
  const data = inventory.data;
  const coins = data?.coins ?? 0;
  const owned = new Set(data?.owned ?? []);
  const equipped = data?.equipped ?? {};

  const purchase = useMutation({
    mutationFn: (cosmeticId: string) => api.purchaseCosmetic(cosmeticId),
    onSuccess: (inv, cosmeticId) => {
      // Keep the wallet + ownership fresh everywhere (the HUD coin chip rides progression()).
      queryClient.setQueryData(['inventory', roomKey], inv);
      void queryClient.invalidateQueries({ queryKey: ['progression'] });
      onNotice(`🪙 Got the ${cosmeticById(cosmeticId)?.name ?? 'item'}! Try it on whenever you like.`);
    },
    onError: (e) => onNotice(errMessage(e)),
  });

  const equip = useMutation({
    mutationFn: ({ slot, cosmeticId }: { slot: CosmeticSlot; cosmeticId: string | null }) =>
      api.equipCosmetic(slot, cosmeticId),
    onSuccess: (inv) => {
      queryClient.setQueryData(['inventory', roomKey], inv);
      // Re-broadcast the freshly-equipped look so everyone in the room sees it (M6).
      onLookChange(inv.equipped);
    },
    onError: (e) => onNotice(errMessage(e)),
  });

  const motionProps = reduceMotion
    ? {}
    : {
        initial: { opacity: 0, x: 24 },
        animate: { opacity: 1, x: 0 },
        exit: { opacity: 0, x: 24 },
        transition: { duration: 0.22, ease: 'easeOut' as const },
      };

  return (
    <motion.section
      ref={sectionRef}
      {...motionProps}
      role="dialog"
      aria-label="Store & Wardrobe"
      tabIndex={-1}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
      // Caps to the viewport height (dvh) so on short tablet landscape (e.g. 1024×768) the catalog
      // scrolls INSIDE the panel instead of running off the bottom; width stays clamped to 92vw.
      className="pointer-events-auto flex max-h-[min(78dvh,42rem)] w-96 max-w-[92vw] flex-col overflow-hidden rounded-2xl border border-white/10 bg-slate-950/85 shadow-2xl outline-none backdrop-blur-xl"
    >
      <header className="flex items-center justify-between gap-3 border-b border-white/10 px-4 py-3">
        <h2 className="flex items-center gap-2 text-sm font-bold text-white">
          <span aria-hidden="true">🛍️</span>
          Store &amp; Wardrobe
        </h2>
        <div className="flex items-center gap-2">
          {/* The wallet, right where you spend it. Dual-coded (icon + number), accessible label. */}
          <span
            aria-label={`You have ${coins} coins`}
            title="Coins are earned from kind, positive actions — and only ever buy looks."
            className="flex items-center gap-1 rounded-full border border-amber-300/30 bg-amber-400/15 px-2.5 py-1 text-xs font-bold text-amber-100"
          >
            <span aria-hidden="true">🪙</span>
            <span>{coins}</span>
          </span>
          <button
            onClick={onClose}
            data-panel-close
            aria-label="Close Store & Wardrobe"
            title="Close"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-white/60 transition hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/50"
          >
            ✕
          </button>
        </div>
      </header>

      {/* Tabs (ТЗ): Looks (skins/items) · Furniture. A simple, accessible two-button switcher. */}
      <div role="tablist" aria-label="Store sections" className="flex gap-1 border-b border-white/10 px-3 pt-2">
        <StoreTab id="looks" active={tab === 'looks'} icon="🧣" label="Looks" onSelect={() => setTab('looks')} />
        <StoreTab id="furniture" active={tab === 'furniture'} icon="🛋️" label="Furniture" onSelect={() => setTab('furniture')} />
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {tab === 'looks' ? (
          <>
            <p className="mb-4 text-xs leading-snug text-white/55">
              Make your duck your own. Everything here is just for looks — never an advantage. Earn coins by
              helping out and celebrating wins, then dress up whenever the mood strikes. No rush. 💛
            </p>

            {inventory.isLoading ? (
              <p className="text-sm text-white/50">Opening the wardrobe…</p>
            ) : inventory.isError ? (
              <p className="text-sm text-red-300">Couldn’t load the store right now — try reopening it.</p>
            ) : (
              COSMETIC_SLOTS.map((slot) => (
                <SlotGroup
                  key={slot}
                  slot={slot}
                  coins={coins}
                  owned={owned}
                  equippedId={equipped[slot]}
                  busy={purchase.isPending || equip.isPending}
                  onBuy={(id) => purchase.mutate(id)}
                  onEquip={(id) => equip.mutate({ slot, cosmeticId: id })}
                />
              ))
            )}
          </>
        ) : (
          <FurnitureTab coins={coins} onArmFurniture={onArmFurniture} />
        )}
      </div>
    </motion.section>
  );
}

/** One Store tab button (Looks / Furniture). Dual-coded, aria-selected, keyboard-focusable. */
function StoreTab({
  id,
  active,
  icon,
  label,
  onSelect,
}: {
  id: string;
  active: boolean;
  icon: string;
  label: string;
  onSelect: () => void;
}): JSX.Element {
  return (
    <button
      role="tab"
      id={`store-tab-${id}`}
      aria-selected={active}
      onClick={onSelect}
      className={`flex items-center gap-1.5 rounded-t-lg px-3 py-2 text-xs font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/50 ${
        active
          ? 'border-b-2 border-amber-300 text-white'
          : 'border-b-2 border-transparent text-white/45 hover:text-white/70'
      }`}
    >
      <span aria-hidden="true">{icon}</span>
      {label}
    </button>
  );
}

/**
 * The FURNITURE tab (ТЗ §1/§3): the buyable furniture catalog grouped by category. Each card shows a
 * preview, name, price, and a "Buy & Place" button — disabled with a gentle reason when short on coins.
 * Clicking it arms the in-world placement ghost (the buy is charged server-side ONLY on a confirmed
 * placement, so cancelling costs nothing — ТЗ §6). Every catalog item has a price, so spam stays gated.
 */
function FurnitureTab({
  coins,
  onArmFurniture,
}: {
  coins: number;
  onArmFurniture: (item: FurnitureItem) => void;
}): JSX.Element {
  return (
    <>
      <p className="mb-4 text-xs leading-snug text-white/55">
        Decorate your room together. Pick a piece, then click where it should go — your coins are only
        spent once it’s placed. Everyone in the room sees it, and it stays put. 🛋️
      </p>
      {FURNITURE_CATEGORIES.map((category) => {
        // `hidden` items are real library SKUs (default-level variants — e.g. the conference-chair facings,
        // server-rack lanes) that ride along the visible representative; keep them out of the browse grid.
        const items = FURNITURE_CATALOG.filter((f) => f.category === category && !f.hidden);
        if (items.length === 0) return null;
        const meta = FURNITURE_CATEGORY_META[category];
        return (
          <section key={category} aria-label={meta.label} className="mb-5 last:mb-0">
            <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-white/45">
              <span aria-hidden="true">{meta.icon}</span>
              {meta.label}
            </h3>
            <ul className="grid grid-cols-2 gap-2">
              {items.map((item) => (
                <li key={item.id}>
                  <FurnitureCard
                    item={item}
                    coins={coins}
                    onBuyPlace={() => onArmFurniture(item)}
                  />
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </>
  );
}

/**
 * One furniture card: emoji preview, name, price, and a "Buy & Place" button. Affordability is a gentle
 * UX hint only — the server is authoritative on price/balance (ТЗ §6/§7), so this never trusts the
 * client. Disabled (with a kind note) when the player can't afford it yet.
 */
function FurnitureCard({
  item,
  coins,
  onBuyPlace,
}: {
  item: FurnitureItem;
  coins: number;
  onBuyPlace: () => void;
}): JSX.Element {
  const affordable = coins >= item.price;
  return (
    <div className="flex h-full flex-col gap-2 rounded-xl border border-white/10 bg-white/5 p-2.5">
      <div className="flex items-center gap-2">
        <span
          aria-hidden="true"
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-slate-900/60 text-2xl"
        >
          {item.icon}
        </span>
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-white" title={item.name}>
            {item.name}
          </div>
          <div className="flex items-center gap-1 text-[11px] font-medium text-amber-200/90">
            <span aria-hidden="true">🪙</span>
            <span>{item.price}</span>
          </div>
        </div>
      </div>
      <div className="mt-auto">
        <button
          onClick={onBuyPlace}
          disabled={!affordable}
          aria-label={
            affordable
              ? `Buy and place ${item.name} for ${item.price} coins`
              : `${item.name} costs ${item.price} coins — keep earning, you’ll get there`
          }
          title={
            affordable
              ? `Buy & place the ${item.name} for ${item.price} coins`
              : `A little more to save — ${item.price - coins} coins to go. No rush. 💛`
          }
          className="flex min-h-[2.25rem] w-full items-center justify-center gap-1 rounded-lg border border-emerald-300/40 bg-emerald-400/15 px-2 py-2 text-xs font-semibold text-emerald-100 transition hover:bg-emerald-400/25 disabled:cursor-not-allowed disabled:border-white/15 disabled:bg-white/10 disabled:text-white/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/50"
        >
          <span aria-hidden="true">{affordable ? '🪑' : '🪙'}</span>
          <span>{affordable ? 'Buy & Place' : 'Keep saving'}</span>
        </button>
      </div>
    </div>
  );
}

/** One slot's section (e.g. "Hats"): a labelled group of cosmetic cards. */
function SlotGroup({
  slot,
  coins,
  owned,
  equippedId,
  busy,
  onBuy,
  onEquip,
}: {
  slot: CosmeticSlot;
  coins: number;
  owned: Set<string>;
  equippedId: string | undefined;
  busy: boolean;
  onBuy: (cosmeticId: string) => void;
  /** Equip the id, or unequip the slot with `null`. */
  onEquip: (cosmeticId: string | null) => void;
}): JSX.Element {
  const meta = SLOT_META[slot];
  const items = cosmeticsBySlot(slot);
  return (
    <section aria-label={meta.label} className="mb-5 last:mb-0">
      <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-white/45">
        <span aria-hidden="true">{meta.icon}</span>
        {meta.label}
      </h3>
      <ul className="grid grid-cols-2 gap-2">
        {items.map((c) => (
          <li key={c.id}>
            <CosmeticCard
              cosmetic={c}
              coins={coins}
              owned={owned.has(c.id)}
              equipped={equippedId === c.id}
              busy={busy}
              onBuy={() => onBuy(c.id)}
              onToggleEquip={() => onEquip(equippedId === c.id ? null : c.id)}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * One cosmetic card: a procedural preview, name + description, price, and the right action. Owned items
 * get an Equip/Unequip toggle (dual-coded, aria-pressed); unowned items get a Buy button that's
 * disabled with a GENTLE, explicit reason (already owned / not enough coins yet) — never a hard sell.
 */
function CosmeticCard({
  cosmetic,
  coins,
  owned,
  equipped,
  busy,
  onBuy,
  onToggleEquip,
}: {
  cosmetic: Cosmetic;
  coins: number;
  owned: boolean;
  equipped: boolean;
  busy: boolean;
  onBuy: () => void;
  onToggleEquip: () => void;
}): JSX.Element {
  const affordable = coins >= cosmetic.price;
  return (
    <div
      className={`flex h-full flex-col gap-2 rounded-xl border p-2.5 transition ${
        equipped ? 'border-amber-300/50 bg-amber-400/10' : 'border-white/10 bg-white/5'
      }`}
    >
      <div className="flex items-center gap-2">
        <CosmeticPreview cosmetic={cosmetic} />
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-white" title={cosmetic.name}>
            {cosmetic.name}
          </div>
          <div className="flex items-center gap-1 text-[11px] font-medium text-amber-200/90">
            <span aria-hidden="true">🪙</span>
            <span>{cosmetic.price}</span>
          </div>
        </div>
      </div>
      <p className="text-[11px] leading-snug text-white/45">{cosmetic.description}</p>

      <div className="mt-auto">
        {owned ? (
          <button
            onClick={onToggleEquip}
            disabled={busy}
            aria-pressed={equipped}
            aria-label={equipped ? `Unequip ${cosmetic.name}` : `Equip ${cosmetic.name}`}
            title={
              equipped
                ? `Take off the ${cosmetic.name}`
                : `Put on the ${cosmetic.name} — everyone will see it`
            }
            className={`flex min-h-[2.25rem] w-full items-center justify-center gap-1 rounded-lg border px-2 py-2 text-xs font-semibold transition disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 ${
              equipped
                ? 'border-amber-300/50 bg-amber-400/25 text-amber-100 hover:bg-amber-400/35 focus-visible:ring-amber-300/50'
                : 'border-emerald-300/40 bg-emerald-400/15 text-emerald-100 hover:bg-emerald-400/25 focus-visible:ring-emerald-300/50'
            }`}
          >
            <span aria-hidden="true">{equipped ? '✓' : '👕'}</span>
            <span>{equipped ? 'Equipped' : 'Equip'}</span>
          </button>
        ) : (
          <button
            onClick={onBuy}
            disabled={busy || !affordable}
            aria-label={
              affordable
                ? `Buy ${cosmetic.name} for ${cosmetic.price} coins`
                : `${cosmetic.name} costs ${cosmetic.price} coins — keep earning, you’ll get there`
            }
            title={
              affordable
                ? `Buy the ${cosmetic.name} for ${cosmetic.price} coins`
                : `A little more to save — ${cosmetic.price - coins} coins to go. No rush. 💛`
            }
            className="flex min-h-[2.25rem] w-full items-center justify-center gap-1 rounded-lg border border-white/15 bg-white/10 px-2 py-2 text-xs font-semibold text-white transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/50"
          >
            <span aria-hidden="true">🪙</span>
            <span>{affordable ? 'Buy' : 'Keep saving'}</span>
          </button>
        )}
      </div>
    </div>
  );
}
